import React, { FC, useCallback, useMemo, useState } from 'react'
import {
  LoaderSpinner,
  Modal,
  ModalHeader,
  SelectedTabProvider,
  Tab,
  TabContent,
  Tabs,
} from '@lightspeed/design-system-react'
import { reactular } from '@vendhq/reactular'
import { vn } from 'vend-number'
import { ApiTransaction } from '@vend/types'
import {
  PAYMENT_TYPES,
  useFeatures,
  usePMOFFeature,
} from '@vend/business-components'
import { removeFromStorage, trackUserEvent } from '@vend/utilities'
import uuid from '@vend/vend-uuid'
import { useT } from '@transifex/react'
import { LSPAYModalOptions, SwiperError } from '../helpers/lspay.types'
import {
  getUnrefRefundReason,
  shouldAllowUnreferencedRefunds,
} from '../helpers/refund'
import { useTrackTransactionDuration } from '../hooks/useTrackTransactionDuration'
import {
  useUserManager,
  useVdCurrencyFilter,
} from '../../../../ng-compat/hooks'
import { getLspayErrorMessage } from '../helpers/errorChecking'
import { useFetchPairing } from '../hooks/useFetchPairing'
import { ACTIVE_LSPAY_TRANSACTION } from '../../../../constants/payments'
import { LSPAY_ERROR } from '../helpers/lspayConstants'
import { WEB_REGISTER } from '../../../web-register.constant'
import { useErrorMessages } from '../hooks/useErrorMessages'
import { useShouldUseSimulator } from '../hooks/useShouldUseSimulator'
import { LSPayFlow } from '../../../sale/payment-panel/PaymentModal/types'
import { useAppSelector } from '../../../../state/hooks'
import { selectSaleCustomer } from '../../../../state/slices/currentSale/currentSaleSlice'
import { isValidCustomer } from '../../../../functions/sale'
import { PERMISSIONS } from '../../../../constants'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { LSPAYRefund } from './refund/LSPAYRefund'
import { LSPAYRefundLegacy } from './refund/LSPAYRefundLegacy'
import { LSPAYRefundServerDriven } from './refund/server-driven/LSPAYRefundServerDriven'
import { LSPAYPaymentTerminal } from './payment/LSPAYPaymentTerminal'
import { LSPAYCardNotPresentPayment } from './payment/LSPAYCardNotPresentPayment'
import { LSPAYCnpPmofPayment } from './payment/LSPAYCnpPmofPayment'
import { ErrorContent } from './modal-states/ErrorContent'
import { LSPAYPaymentServerDriven } from './payment/lspay-payment-server-driven/LSPAYPaymentServerDriven'
import { LSPAYPaymentMethodOnFile } from './payment/LSPAYPaymentMethodOnFile'

interface LSPAYPaymentModalProps extends LSPAYModalOptions {
  close: (val?: Promise<ApiTransaction>) => void
  // Indicates that only the specified payment workflow is rendered to the modal.
  // Terminal, Card-not-present Manual Entry or Payment Method on File.
  forceFlow?: LSPayFlow
  paymentMethodOnFileId?: string
}

export const LSPAYPaymentModal: FC<LSPAYPaymentModalProps> = ({
  amount: rawAmount,
  outletId,
  currency,
  register,
  registerSaleId,
  originalRegisterSalePaymentIds,
  originalSalePaidByOtherPT,
  originalSaleHasProcessingServicePayment,
  retailerPaymentType,
  transactionId: initialTransactionId,
  paymentIntentId,
  isRecovery,
  withSurcharge,
  tipEligibleAmount: rawTipEligibleAmount,
  close: originalClose,
  forceFlow,
  paymentMethodOnFileId,
}) => {
  const [transactionId, setTransactionId] = useState<string>(
    initialTransactionId
  )
  const [previousSelectedTab, setPreviousSelectedTab] = useState<string>(
    'terminal'
  )
  const [features, featuresLoading] = useFeatures(
    [
      'lspay_unreferenced_refund',
      'lspay_international_unreferenced_refunds',
      'lspay_cnp_payments',
      'lspay_cnp_moto',
      'lspay_retrieve_pi_on_error',
      'lspay_refund_with_server_driven',
      'lspay_referenced_refund_with_server_driven',
      'enable_customer_saved_cards',
    ],
    {
      lspay_unreferenced_refund: false,
      lspay_international_unreferenced_refunds: false,
      lspay_cnp_payments: false,
      lspay_cnp_moto: false,
      lspay_retrieve_pi_on_error: true,
      lspay_refund_with_server_driven: false,
      lspay_referenced_refund_with_server_driven: false,
      enable_customer_saved_cards: false,
    }
  )

  const {
    loading: pairingLoading,
    pairing,
    error: pairingError,
    refetch: refetchPairing,
    isServerDrivenSupported: payWithServerDriven,
  } = useFetchPairing(register.id)

  const shouldUseSimulator = useShouldUseSimulator()
  const vdCurrencyFilter = useVdCurrencyFilter()
  const [dismissible, setDismissible] = useState<boolean>(false)
  const [disableCNP, setDisableCNP] = useState<boolean>(false)
  const [disableTerminalPayment, setDisableTerminalPayment] = useState<boolean>(
    false
  )
  const [intentClientSecret, setIntentClientSecret] = useState<string>()
  const [swiperError, setSwiperError] = useState<SwiperError>()
  const [cancelProcessing, setCancelProcessing] = useState<
    (() => void) | undefined
  >(undefined)
  const amount = useMemo(() => vn(rawAmount), [rawAmount])
  const tipEligibleAmount = useMemo(() => vn(rawTipEligibleAmount), [
    rawTipEligibleAmount,
  ])
  const isRefund = amount.isNegative()

  const trackEnd = useTrackTransactionDuration({
    registerId: register.id,
    transactionId: initialTransactionId,
    paymentTypeId: PAYMENT_TYPES.LSPAY,
    isRefund,
    amount: amount.toNumber(),
  })

  const saleCustomer = useAppSelector(selectSaleCustomer)
  const customerId = isValidCustomer(saleCustomer) ? saleCustomer.id : undefined

  const getErrorMessage = useErrorMessages()
  const t = useT()

  const close = useCallback(
    (val?: Promise<ApiTransaction>) => {
      // Send transaction finished ube when modal is closed.
      // If there was a transaction, the payment was successfully completed, otherwise it failed
      const completed = !!val
      trackEnd(completed)

      originalClose(val)
    },
    [originalClose, trackEnd]
  )
  const generateTransactionId = useCallback(() => {
    const transactionId = uuid()
    setTransactionId(transactionId)
    return transactionId
  }, [])

  const isLoading = featuresLoading || pairingLoading

  // Payment method on file related variables
  const { isEnabled: lspayPMOFSettingEnabled } = usePMOFFeature()
  const lspayPMOFuserPermissionGranted = useUserManager().hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )
  const trainingMode = useAppSelector(selectTrainingModeEnabled)

  // Track the user event only if the payment is not initiated with server-driven
  if (!isLoading && !payWithServerDriven) {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSPAY Client Driven flow should start',
      {
        payWithServerDriven,
        pairing,
        pairingLoading,
      }
    )
  }

  const onError = useCallback(() => {
    if (payWithServerDriven) {
      // If the payment was initiated with server-driven, we need to remove the active transaction from storage
      removeFromStorage(ACTIVE_LSPAY_TRANSACTION)
    }
    // close the popup with no argument - it is picked up as a failure
    close()
  }, [close, payWithServerDriven])

  if (isLoading) {
    return (
      <Modal size="medium" focusTrapActive={true}>
        <div
          className="vd-flex vd-flex--justify-center"
          style={{ height: '100%' }}
        >
          <LoaderSpinner />
        </div>
      </Modal>
    )
  }

  if (swiperError || pairingError) {
    const lspayError = swiperError
      ? getLspayErrorMessage({
          err: swiperError,
          vdCurrencyFilter,
          retailerPaymentTypeName: retailerPaymentType.name,
          t,
          getErrorMessage,
        })
      : getErrorMessage(LSPAY_ERROR.DEFAULT_PAYMENT)
    return (
      <Modal size="medium" focusTrapActive={true} dismissable={true}>
        <ErrorContent
          disableRetry={!pairingError} // Retry only if there is a pairing error
          retry={pairingError ? () => refetchPairing() : () => {}}
          close={close}
          lspayError={lspayError}
        />
      </Modal>
    )
  }

  const {
    lspay_unreferenced_refund: lspayUnreferencedRefundFlag,
    lspay_cnp_payments: lspayCNPPaymentsFlag,
    lspay_cnp_moto: lspayCNPMotoFlag,
    lspay_international_unreferenced_refunds: lspayInternationalUnreferencedRefundFeatureFlag,
    lspay_refund_with_server_driven: lspayRefundWithServerDriven,
    lspay_referenced_refund_with_server_driven: lspayReferencedRefundWithServerDriven,
    enable_customer_saved_cards: lspayPMOFFeatureFlag,
  } = features

  const isSettingEnabledByMerchantAndFlagEnabled =
    lspayPMOFFeatureFlag && lspayPMOFSettingEnabled

  const isCurrentUserSessionEligibleForPMOF =
    lspayPMOFuserPermissionGranted && !trainingMode

  const isCustomerSetOnSale = !!customerId

  const useProcessingPMOF =
    isSettingEnabledByMerchantAndFlagEnabled &&
    isCurrentUserSessionEligibleForPMOF &&
    isCustomerSetOnSale

  const allowUnrefRefund = shouldAllowUnreferencedRefunds(
    lspayUnreferencedRefundFlag,
    lspayInternationalUnreferencedRefundFeatureFlag,
    currency
  )

  // For server-driven refunds, we consider both referenced and unreferenced refunds.
  // The function `getUnrefRefundReason` will return a reason if it is an unreferenced refund.
  // We have developed two phases for refunds:
  // 1. lspayRefundWithServerDriven for unreferenced refunds.
  // 2. lspayReferencedRefundWithServerDriven for referenced refunds.
  // if both are enabled, all refunds will be server-driven.
  // However, PMOF payments must use the legacy flow as the server-driven flow
  // doesn't support Processing Service refunds.
  const shouldServerDrivenRefund =
    isRefund && // Refund check
    !originalSaleHasProcessingServicePayment && // Processing Service payments must use legacy flow
    payWithServerDriven && // Server-driven check, which is set based on pairing
    lspayRefundWithServerDriven && // FF check
    (lspayReferencedRefundWithServerDriven || // FF check for referenced refund
      !!getUnrefRefundReason(
        // Unreferenced refund check
        originalRegisterSalePaymentIds,
        originalSalePaidByOtherPT
      ))

  const showHeader = !isRefund && lspayCNPPaymentsFlag

  const lspayRegister = {
    id: register.id,
    name: register.name,
  }

  const PaymentComponent =
    payWithServerDriven && !shouldUseSimulator
      ? LSPAYPaymentServerDriven
      : LSPAYPaymentTerminal

  // For refunds, use the legacy component if:
  // 1. Server-driven refund conditions are not met, OR
  // 2. Using simulator, OR
  // 3. The original payment was processed via Processing Service
  const RefundComponent =
    shouldServerDrivenRefund && !shouldUseSimulator
      ? LSPAYRefundServerDriven
      : LSPAYRefund

  const handleOnChange = (selectedTab: string) => {
    if (previousSelectedTab !== selectedTab && payWithServerDriven) {
      if (selectedTab === 'terminal') {
        const oldRegisterSalePaymentId = transactionId

        const newRegisterSalePaymentId = generateTransactionId()

        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD user switched to terminal tab',
          {
            oldRegisterSalePaymentId,
            newRegisterSalePaymentId,
            registerSaleId,
          }
        )
      } else {
        //   If the payment was initiated and switched to CNP (Card Not Present) tab,
        //   we don't need to recover the transaction; the transaction will be canceled when switching tabs.
        //   Additionally, if the user switches back to the terminal tab,
        //   we generate a new transactionId as we no longer need to keep the old one.
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD user switched to cnp tab',
          {
            registerSalePaymentId: transactionId,
            registerSaleId,
          }
        )
        removeFromStorage(ACTIVE_LSPAY_TRANSACTION)
      }
    }

    setPreviousSelectedTab(selectedTab)
  }

  return (
    <Modal
      size="medium"
      focusTrapActive={true}
      dismissable={dismissible}
      onClose={onError}
      className="lspay-payment-modal"
    >
      {showHeader && (
        <ModalHeader>
          {t('Pay {amount} with {retailerPaymentTypeName}', {
            amount: vdCurrencyFilter(amount.toNumber()),
            retailerPaymentTypeName: retailerPaymentType.name,
            _context: 'Payment modal header',
          })}
        </ModalHeader>
      )}
      {isRefund ? (
        <>
          {/* If server-driven flow is enabled, every refund should go through the server-driven component regardless of allowUnref value.
              Once we roll out the server-driven flow completely, we can remove the legacy component. */}
          {allowUnrefRefund || shouldServerDrivenRefund ? (
            <RefundComponent
              currency={currency}
              onSuccess={close}
              onError={onError}
              amount={amount}
              disableUnrefRefund={!allowUnrefRefund}
              registerSaleId={registerSaleId}
              originalRegisterSalePaymentIds={originalRegisterSalePaymentIds}
              originalSalePaidByOtherPT={originalSalePaidByOtherPT}
              register={lspayRegister}
              outletId={outletId}
              transactionId={transactionId}
              retailerPaymentType={retailerPaymentType}
              setDismissible={setDismissible}
              generateTransactionId={generateTransactionId}
              pairing={pairing}
            />
          ) : (
            <LSPAYRefundLegacy
              currency={currency}
              onSuccess={close}
              onError={onError}
              amount={amount}
              originalRegisterSalePaymentId={originalRegisterSalePaymentIds![0]}
              register={lspayRegister}
              outletId={outletId}
              transactionId={initialTransactionId}
              retailerPaymentType={retailerPaymentType}
            />
          )}
        </>
      ) : lspayCNPPaymentsFlag && !forceFlow ? (
        <SelectedTabProvider
          defaultTab="terminal"
          onChange={selectedTab => handleOnChange(selectedTab)}
        >
          <Tabs className="vd-ml8 vd-mr8">
            <Tab
              data-track="tap-insert-or-swipe-card"
              name="terminal"
              disabled={disableTerminalPayment}
            >
              {t('Tap, insert, or swipe card', {
                _context:
                  'Tab label in the payment modal for transactions using a physical card',
              })}
            </Tab>
            <Tab
              data-track="enter-card-details"
              name="cnp"
              disabled={disableCNP}
            >
              {t('Enter card details manually', {
                _context:
                  'Tab label in the payment modal for entering card details manually',
              })}
            </Tab>
          </Tabs>
          <TabContent name="terminal">
            <PaymentComponent
              onSuccess={close}
              onError={onError}
              amount={amount}
              outletId={outletId}
              register={register}
              registerSaleId={registerSaleId}
              retailerPaymentType={retailerPaymentType}
              transactionId={transactionId}
              currency={currency}
              setDismissible={setDismissible}
              setDisableCNP={setDisableCNP}
              setIntentClientSecret={setIntentClientSecret}
              setSwiperError={setSwiperError}
              paymentIntentId={paymentIntentId}
              isRecovery={isRecovery}
              withSurcharge={withSurcharge}
              {...(tipEligibleAmount?.toNumber() !== 0
                ? { tipEligibleAmount }
                : {})}
              pairing={pairing}
              setCancelProcessing={setCancelProcessing}
              generateTransactionId={generateTransactionId}
              shouldUseSimulator={shouldUseSimulator}
            />
          </TabContent>
          <TabContent name="cnp">
            {useProcessingPMOF ? (
              <LSPAYCnpPmofPayment
                amount={amount}
                customerId={customerId}
                register={register}
                registerSaleId={registerSaleId}
                registerSalePaymentId={transactionId}
                retailerPaymentTypeId={retailerPaymentType.id}
                setSwiperError={setSwiperError}
                setDisableTerminalPayment={setDisableTerminalPayment}
                onError={onError}
                onSuccess={close}
                onClose={close}
                cancelProcessing={cancelProcessing}
              />
            ) : (
              <LSPAYCardNotPresentPayment
                setDisableTerminalPayment={setDisableTerminalPayment}
                close={close}
                outletId={outletId}
                amount={amount}
                currency={currency}
                register={register}
                retailerPaymentTypeId={retailerPaymentType.id}
                transactionId={initialTransactionId}
                intentClientSecret={intentClientSecret}
                setSwiperError={setSwiperError}
                hasMoto={lspayCNPMotoFlag}
                cancelProcessing={cancelProcessing}
              />
            )}
          </TabContent>
        </SelectedTabProvider>
      ) : forceFlow === 'pmof' && paymentMethodOnFileId && customerId ? (
        <LSPAYPaymentMethodOnFile
          amount={amount}
          customerId={customerId}
          paymentMethodOnFileId={paymentMethodOnFileId}
          register={register}
          registerSaleId={registerSaleId}
          transactionId={transactionId}
          close={close}
          setDismissible={setDismissible}
          setSwiperError={setSwiperError}
        />
      ) : (
        <PaymentComponent
          onSuccess={close}
          onError={onError}
          amount={amount}
          outletId={outletId}
          register={register}
          registerSaleId={registerSaleId}
          retailerPaymentType={retailerPaymentType}
          transactionId={transactionId}
          currency={currency}
          setDismissible={setDismissible}
          setDisableCNP={setDisableCNP}
          setIntentClientSecret={setIntentClientSecret}
          setSwiperError={setSwiperError}
          paymentIntentId={paymentIntentId}
          isRecovery={isRecovery}
          withSurcharge={withSurcharge}
          {...(tipEligibleAmount?.toNumber() !== 0
            ? { tipEligibleAmount }
            : {})}
          pairing={pairing}
          setCancelProcessing={setCancelProcessing}
          generateTransactionId={generateTransactionId}
        />
      )}
    </Modal>
  )
}

export const LSPAYPaymentModalComponent = reactular(
  LSPAYPaymentModal,
  [
    'amount',
    'outletId',
    'currency',
    'register',
    'registerSaleId',
    'originalRegisterSalePaymentIds',
    'originalSalePaidByOtherPT',
    'originalSaleHasProcessingServicePayment',
    'retailerPaymentType',
    'transactionId',
    'paymentIntentId',
    'isRecovery',
    'withSurcharge',
    'tipEligibleAmount',
    'close',
  ],
  'reactWrapper'
)
